/**
 * Shared wall-clock cooldown + test-mode helpers for ad placements that
 * don't already have their own cooldown mechanism (video preroll, inline
 * grid ads, popunder). The interstitial has its own equivalent built in.
 *
 * Cooldown works alongside each placement's existing count-based frequency
 * ("every N videos/posts") — both must pass for an ad to actually show.
 * A cooldown_minutes of 0 disables the time check entirely.
 */

import { isGlobalForceAdsOn } from "@/lib/global-force-ads"
import { isGlobalAdsOff } from "@/lib/global-ads-off"

const COOLDOWN_PREFIX = "goonru_ad_cooldown_"
const TEST_MODE_KEY = "goonru_ad_test_mode"

export function isAdCooldownExpired(adType: string, cooldownMinutes: number): boolean {
  if (isAdTestModeOn()) return true
  if (!cooldownMinutes || cooldownMinutes <= 0) return true
  try {
    const last = localStorage.getItem(COOLDOWN_PREFIX + adType)
    if (!last) return true
    return (Date.now() - parseInt(last, 10)) / 60000 >= cooldownMinutes
  } catch {
    return true
  }
}

export function markAdShown(adType: string): void {
  try {
    localStorage.setItem(COOLDOWN_PREFIX + adType, String(Date.now()))
  } catch {
    // best-effort
  }
}

/**
 * Test mode forces every ad placement to ignore its frequency/cooldown
 * checks and show on the very next opportunity.
 *
 * Two independent sources, either one is enough:
 * - The localStorage flag below: admin-only, session-local, never affects
 *   other visitors. Set via the toggle in AdTestPanel.tsx.
 * - isGlobalForceAdsOn() (lib/global-force-ads.ts): a DB-backed flag a
 *   super-admin can flip to force this for EVERY visitor sitewide — for
 *   confirming ads actually render on a real guest session, a different
 *   account, or a device the admin isn't logged into, which the
 *   localStorage-only flag can't reach. Checking it here means every
 *   existing isAdTestModeOn() call site (there are many) picks it up with
 *   no changes needed anywhere else.
 */
export function isAdTestModeOn(): boolean {
  // Sitewide Force Ads OFF (lib/global-ads-off.ts) wins over every force-on flag.
  if (isGlobalAdsOff()) return false
  try {
    if (localStorage.getItem(TEST_MODE_KEY) === "1") return true
  } catch {
    // fall through to the global check
  }
  return isGlobalForceAdsOn()
}

export function setAdTestMode(on: boolean): void {
  try {
    if (on) localStorage.setItem(TEST_MODE_KEY, "1")
    else localStorage.removeItem(TEST_MODE_KEY)
  } catch {
    // best-effort
  }
}

const SESSION_START_KEY = "goonru_session_start"

/**
 * When this browser tab's session actually started — sessionStorage, so a
 * new tab/window gets a fresh clock but reloading the same tab doesn't reset
 * it. Backs the "no interstitial until the session is N minutes old" gate —
 * arriving on the site and immediately eating a full-screen ad reads as
 * hostile even when the post-count/cooldown checks both technically pass.
 */
function getSessionStartMs(): number {
  try {
    const existing = sessionStorage.getItem(SESSION_START_KEY)
    if (existing) return parseInt(existing, 10)
    const now = Date.now()
    sessionStorage.setItem(SESSION_START_KEY, String(now))
    return now
  } catch {
    return Date.now()
  }
}

export function isSessionOldEnoughForAds(minSessionMinutes: number): boolean {
  if (isAdTestModeOn()) return true
  if (!minSessionMinutes || minSessionMinutes <= 0) return true
  return (Date.now() - getSessionStartMs()) / 60000 >= minSessionMinutes
}

/** How long this tab's session has been open, in ms — lets a caller compute
 * how much longer to wait (e.g. popunder-injector.tsx's delay_minutes)
 * instead of only getting a yes/no from isSessionOldEnoughForAds. */
export function getSessionAgeMs(): number {
  return Date.now() - getSessionStartMs()
}

const VIEW_COUNT_PREFIX = "goonru_ad_view_count_"

/**
 * Count-based "every N opportunities" frequency, for placements whose admin
 * config is a raw count (e.g. video_ads_config.letterbox_frequency) rather
 * than a wall-clock cooldown. Persists per-browser via localStorage, same
 * as the cooldown helpers above.
 */
export function getAdViewCount(adType: string): number {
  try {
    return parseInt(localStorage.getItem(VIEW_COUNT_PREFIX + adType) ?? "0", 10)
  } catch {
    return 0
  }
}

export function setAdViewCount(adType: string, n: number): void {
  try {
    localStorage.setItem(VIEW_COUNT_PREFIX + adType, String(n))
  } catch {
    // best-effort
  }
}
